import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Landmark,
  Sun,
  Moon,
  Laptop,
  Menu,
  X,
  AlertCircle,
  PlusCircle,
  Library,
  Settings,
  LogOut,
  Building,
  FileText,
  BarChart2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { MacroDashLogo } from '../components/MacroDashLogo';
import { useAuth } from '../hooks/useAuth';
import { useUnread } from '../hooks/useUnread';
import { apiRequest } from '../lib/api';
import { usePressMinistries } from '../hooks/usePressReleases';

type ThemeMode = 'light' | 'dark' | 'system';

export function MainLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { unreadSummaryCount } = useUnread();
  const { ministries } = usePressMinistries();

  const [theme, setTheme] = useState<ThemeMode>(() => (localStorage.getItem('theme') as ThemeMode) || 'system');
  // isUserChange gates write-back: only persist when the user explicitly changes a setting
  const isUserChange = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { data: apiSettings } = useSWR<{ theme?: string }>(
    user ? '/api/settings' : null,
    apiRequest
  );

  useEffect(() => {
    if (!apiSettings) return;
    if (apiSettings.theme && apiSettings.theme !== theme) setTheme(apiSettings.theme as ThemeMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSettings]);

  // Clear any legacy root font-size override from the old setting
  useEffect(() => {
    document.documentElement.style.fontSize = '';
    localStorage.removeItem('fontSize');
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = (mode: ThemeMode) => {
      root.classList.remove('dark');
      if (mode === 'dark') {
        root.classList.add('dark');
      } else if (mode === 'system') {
        if (mediaQuery.matches) {
          root.classList.add('dark');
        }
      }
    };

    applyTheme(theme);
    if (isUserChange.current) {
      isUserChange.current = false;
      localStorage.setItem('theme', theme);
      if (userRef.current) {
        apiRequest('/api/settings', {
          method: 'PUT',
          body: JSON.stringify({ theme }),
        }).catch((err) => console.error(err));
      }
    }

    const handleSystemChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        if (e.matches) root.classList.add('dark');
        else root.classList.remove('dark');
      }
    };

    mediaQuery.addEventListener('change', handleSystemChange);
    return () => mediaQuery.removeEventListener('change', handleSystemChange);
  }, [theme]);

  const toggleTheme = () => {
    isUserChange.current = true;
    const modes: ThemeMode[] = ['light', 'dark', 'system'];
    const nextIndex = (modes.indexOf(theme) + 1) % modes.length;
    setTheme(modes[nextIndex]);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light': return <Sun size={14} className="mr-2" />;
      case 'dark': return <Moon size={14} className="mr-2" />;
      case 'system': return <Laptop size={14} className="mr-2" />;
    }
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light': return 'Light Mode';
      case 'dark': return 'Dark Mode';
      case 'system': return 'System Auto';
    }
  };

  return (
    <div className="flex min-h-dvh bg-app-bg text-text-primary font-sans selection:bg-orange-bg selection:text-ink">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar — mypf shell: 200px, warm sidebar-bg, 3px orange left rail */}
      <aside className={cn(
        "w-[200px] bg-sidebar-bg flex flex-col fixed h-full transition-transform duration-300 z-40 lg:translate-x-0",
        "border-r border-rule-soft",
        isSidebarOpen ? "translate-x-0 shadow-xl" : "-translate-x-full"
      )}>
        <div aria-hidden className="absolute top-0 left-0 h-full w-[3px] bg-orange" />

        <div className="h-10 flex items-center justify-between px-4 border-b border-rule-soft">
          <NavLink
            to="/my-dash/library"
            className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-ink hover:text-ink transition-colors"
          >
            <span className="text-orange" aria-hidden>
              <MacroDashLogo size={16} />
            </span>
            <span>MacroDash</span>
          </NavLink>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-rule-soft rounded"
            aria-label="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 py-3 px-2 space-y-0.5 overflow-y-auto no-scrollbar">
          <div className="px-2 py-1.5 text-xs uppercase text-ink-3 font-bold tracking-widest">Dashboards</div>
          <NavItem to="/my-dash/library" icon={<Library size={14} />} label="Library" onClick={() => setIsSidebarOpen(false)} isBold={unreadSummaryCount > 0} />
          <NavItem to="/my-dash/x-accounts" icon={<svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>} label="Posts" onClick={() => setIsSidebarOpen(false)} />
          <NavItem to="/my-dash/yt-channels" icon={<svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>} label="YouTube" onClick={() => setIsSidebarOpen(false)} />
          <NavItem to="/my-dash/corporates" icon={<Building size={14} />} label="Corporates" onClick={() => setIsSidebarOpen(false)} />
          <NavItem to="/my-dash/submit" icon={<PlusCircle size={14} />} label="Submit" onClick={() => setIsSidebarOpen(false)} />

          <div className="border-b border-rule-soft my-4 mx-2" />

          <div className="px-2 py-1.5 text-xs uppercase text-ink-3 font-bold tracking-widest">Documents</div>
          <NavItem to="/documents/files" icon={<FileText size={14} />} label="My Docs" onClick={() => setIsSidebarOpen(false)} />
          <NavItem to="/documents/submit" icon={<PlusCircle size={14} />} label="Upload" onClick={() => setIsSidebarOpen(false)} />

          <div className="border-b border-rule-soft my-4 mx-2" />

          <div className="px-2 py-1.5 text-xs uppercase text-ink-3 font-bold tracking-widest">Ministries</div>
          {ministries.map((m) => (
            <NavItem
              key={m.slug}
              to={`/press/${m.slug}`}
              icon={<Landmark size={14} />}
              label={m.shortName}
              onClick={() => setIsSidebarOpen(false)}
            />
          ))}
        </nav>

        <div className="pt-2 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-rule-soft space-y-0.5">
          {user ? (
            <>
              <NavItem to="/my-dash/settings" icon={<Settings size={14} />} label="Settings" onClick={() => setIsSidebarOpen(false)} />
              <NavItem to="/my-dash/usage" icon={<BarChart2 size={14} />} label="Usage" onClick={() => setIsSidebarOpen(false)} />
              <button
                onClick={signOut}
                className="flex items-center w-full px-2 py-2 text-sm text-ink-2 hover:bg-red-bg hover:text-red rounded transition-colors select-none cursor-pointer"
              >
                <span className="mr-2 text-ink-3"><LogOut size={14} /></span>
                Sign Out
              </button>
            </>
          ) : (
            <NavItem to="/login" icon={<LogOut size={14} className="rotate-180" />} label="Sign In" onClick={() => setIsSidebarOpen(false)} />
          )}

          <NavItem to="/disclaimer" icon={<AlertCircle size={14} />} label="Disclaimer" onClick={() => setIsSidebarOpen(false)} />
          <NavItem to="/about" icon={<span className="text-ink-3 text-xs font-sans">i</span>} label="About" onClick={() => setIsSidebarOpen(false)} />

          <button
            onClick={toggleTheme}
            className="flex items-center w-full px-2 py-2 text-sm text-ink-2 hover:bg-rule-soft hover:text-ink rounded transition-colors select-none cursor-pointer"
            aria-label={`Toggle theme (current: ${theme})`}
          >
            {getThemeIcon()}
            {getThemeLabel()}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:ml-[200px] overflow-x-clip">
        <header className="h-10 bg-app-bg border-b border-rule-soft sticky top-0 z-20 flex items-center pl-[max(1rem,env(safe-area-inset-left))] pr-4 justify-between">
          <div className="flex items-center space-x-4 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 hover:bg-rule-soft rounded transition-colors cursor-pointer text-ink-2 shrink-0"
              aria-label="Open sidebar"
            >
              <Menu size={18} />
            </button>

            <div className="text-[13px] text-ink-3 flex items-center truncate font-sans">
              <NavLink to="/" className="hover:text-ink transition-colors">
                <span className="text-ink-3">/</span>
                <span className="mx-1">home</span>
              </NavLink>
              {location.pathname.split('/').filter(Boolean).map((segment, index, arr) => {
                const path = '/' + arr.slice(0, index + 1).join('/');
                const isLast = index === arr.length - 1;
                const labelMap: Record<string, string> = {
                  'my-dash': 'My Dash',
                  'library': 'Library',
                  'submit': 'Submit',
                  'search': 'Search',
                  'settings': 'Settings',
                  'jobs': 'Jobs',
                  'ministry': 'Ministry',
                  'power': 'Power',
                  'finance': 'Finance',
                  'commerce': 'Commerce',
                  'corporates': 'Corporates',
                  'documents': 'Documents',
                  'files': 'My Docs',
                  'usage': 'Usage',
                  'about': 'About',
                };
                const label = (labelMap[segment] || segment).toLowerCase();

                return (
                  <React.Fragment key={index}>
                    <span className="text-ink-3">/</span>
                    {isLast ? (
                      <span className="mx-1 text-ink font-semibold">{label}</span>
                    ) : (
                      <NavLink to={path} className="mx-1 hover:text-ink transition-colors">
                        {label}
                      </NavLink>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-6 w-full">
          {children}
        </div>
      </main>
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  onClick,
  isBold,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  isBold?: boolean;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center px-2 py-2 rounded text-sm transition-colors min-w-0',
          isActive
            ? 'bg-bg-elev text-ink border border-rule-soft'
            : 'text-ink-2 border border-transparent hover:bg-rule-soft hover:text-ink'
        )
      }
    >
      <span className="mr-2 text-ink-3">{icon}</span>
      <span className={cn('truncate', isBold && 'font-semibold')}>{label}</span>
      {isBold && <span className="size-1.5 rounded-full bg-orange ml-2" />}
    </NavLink>
  );
}
